/**
 * One resource, read the way a modder reads it.
 *
 * The pieces of this layer each answer one question — what does the manifest
 * declare, which names does the vehicle metadata use, what is in `stream/`,
 * which resources does this need. None of them is where the interesting bugs
 * live. The interesting bugs live between them: a `data_file` line naming a file
 * that is on disk but not in `files{}`, a `handlingId` pointing at an entry
 * nobody wrote, a mod kit id that is fine in isolation and is also the one stock
 * GTA uses for the entire police fleet.
 *
 * So this module is the join. It runs the analyzers, resolves what they say
 * against each other, and produces one report whose findings are ordered by how
 * much they will hurt.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO RULES THAT SHAPE EVERYTHING HERE
 *
 * A reference that points outside the folder is not broken. `audioNameHash
 * sultan`, a base-game layout, a siren profile the resource does not define —
 * all of those are correct, and a checker that flags them cries wolf on every
 * working resource until nobody reads it. The vanilla tables are only ever used
 * to *confirm* that something is a base-game name. They are never used to demote
 * an unknown name to missing, because "not in my table" and "not in the game"
 * are different statements.
 *
 * A check that could not run did not pass. When the caller did not supply the
 * vanilla index, or the resource ships no `carcols.meta`, the rules that need
 * them are recorded in `skipped` and said out loud. A report that quietly drops
 * half its rules and shows a green tick is worse than no report.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  dataFileKindFor,
  expectedRootFor,
  isKnownDataFileType,
  isStreamExtension,
} from "./data/dataFileKinds.v1";
import { buildDependencyGraph, dependencyFindings } from "./dependencies";
import { dedupeFindings, sortFindings, verdictFor } from "./findings";
import {
  declaredPaths,
  entriesOfKind,
  isGlobPattern,
  manifestPatternToRegExp,
  parseManifest,
  parseResourceInclude,
} from "./manifest";
import { disambiguateLabels, PROSE_LABEL_STYLE } from "./labels";
import {
  baseName,
  caseOnlyMatch,
  closestMatch,
  isInStreamFolder,
  normalizeName,
  pathKey,
} from "./normalize";
import { analyzeStreamAssets, collectStreamAssets } from "./streamAssets";
import { analyzeVehicleMeta } from "./vehicleMeta";
import type {
  DependencyGraph,
  GtaFinding,
  GtaResourceReport,
  GtaResourceInput,
  ManifestEntry,
  ParsedManifest,
  StreamAsset,
  VanillaAssetNameIndex,
  VehicleEntity,
} from "./types";
import { FX_VERSIONS } from "./types";

// ─── Options ─────────────────────────────────────────────────────────────────

export type AnalyzeOptions = {
  /**
   * The stock asset name index, when the caller loaded it.
   *
   * Absent is a normal, supported state: loading 238k names costs 850 KB and
   * most sessions never run a vanilla check. Absent means the vanilla rules are
   * skipped and said so, never that they passed.
   */
  vanillaNames?: VanillaAssetNameIndex;
  /**
   * The words for each resource, keyed by lower-cased identity.
   *
   * Only a caller holding several resources can fill this in, and that is the
   * whole reason it exists. `analyzeResource` sees one resource and therefore
   * cannot know that the report it is contributing to holds a second pack with
   * the same name — so `police ships extra.yft at 2 paths` came out of it true
   * of both packs and actionable on neither. `analyzeResourceSet` knows, and
   * passes what it knows down rather than letting each resource guess.
   *
   * Optional, and a caller that omits it gets exactly what it got before: the
   * bare name, which is the right answer when there is only one of it.
   */
  resourceLabels?: ReadonlyMap<string, string>;
};

/** The manifest file names FiveM will look for, newest first. */
export const MANIFEST_FILE_NAMES: readonly string[] = ["fxmanifest.lua", "__resource.lua"];

export function isManifestFileName(fileName: string): boolean {
  return MANIFEST_FILE_NAMES.includes(fileName.trim().toLowerCase());
}

// ─── Manifest rules ──────────────────────────────────────────────────────────

/**
 * Which severity a missing path deserves depends on what declared it.
 *
 * A script the resource cannot find stops it from starting. A file in `files{}`
 * that is not there ships a resource that runs and is missing something. Saying
 * both are "error" would be easier and would make the first one harder to see.
 */
const MISSING_PATH_SEVERITY: Record<string, "error" | "warning"> = {
  client_script: "error",
  server_script: "error",
  shared_script: "error",
  ui_page: "error",
  data_file: "error",
  file: "warning",
};

export function analyzeManifest(
  input: GtaResourceInput,
  manifest: ParsedManifest,
): { findings: GtaFinding[]; skipped: string[] } {
  const findings: GtaFinding[] = [];
  const skipped: string[] = [];

  const byKey = new Map(input.files.map((f) => [pathKey(f.path), f]));
  const originalPaths = input.files.map((f) => f.path);
  const manifestEntity = { kind: "manifest" as const, id: manifest.file };

  const at = (entry: ManifestEntry) => ({
    file: manifest.file,
    line: entry.span.line,
    column: entry.span.column,
    byteStart: entry.span.byteStart,
    byteEnd: entry.span.byteEnd,
  });

  // ── Header declarations ──────────────────────────────────────────────────

  if (manifest.legacy) {
    findings.push({
      code: "manifest.legacy",
      severity: "warning",
      title: "Legacy manifest format",
      detail:
        "This uses the pre-2019 `__resource.lua` / `resource_manifest_version` form. Current FiveM " +
        "builds still load it but warn. Rename the file to `fxmanifest.lua` and replace the version " +
        "GUID with `fx_version 'cerulean'` and `game 'gta5'`; the files{}, data_file and script lines " +
        "carry over unchanged.",
      file: manifest.file,
      entity: manifestEntity,
      fix: { label: "Rename to fxmanifest.lua and add fx_version 'cerulean'", confidence: "medium" },
    });
  } else if (manifest.fxVersion === null) {
    findings.push({
      code: "manifest.fx_version.missing",
      severity: "error",
      title: "No fx_version",
      detail:
        "Without an `fx_version` line FiveM refuses to start the resource at all. Add " +
        "`fx_version 'cerulean'` at the top of the manifest.",
      file: manifest.file,
      entity: manifestEntity,
    });
  } else if (!FX_VERSIONS.includes(manifest.fxVersion.toLowerCase())) {
    findings.push({
      code: "manifest.fx_version.unknown",
      severity: "warning",
      title: `fx_version '${manifest.fxVersion}' is not one FiveM knows`,
      detail:
        `The accepted values are ${FX_VERSIONS.join(", ")}. Anything else falls back to the oldest ` +
        "behaviour, which quietly changes how scripts are loaded. Use `cerulean` unless you have a " +
        "reason not to.",
      file: manifest.file,
      entity: manifestEntity,
    });
  }

  if (manifest.games.length === 0 && !manifest.legacy) {
    findings.push({
      code: "manifest.game.missing",
      severity: "warning",
      title: "No game declaration",
      detail:
        "Without `game 'gta5'` the resource declares no target game, and a server running a mixed " +
        "set of resources may skip it. Add `game 'gta5'` next to the fx_version line.",
      file: manifest.file,
      entity: manifestEntity,
    });
  }

  // ── Duplicate declarations ───────────────────────────────────────────────

  const seenEntries = new Set<string>();
  for (const entry of manifest.entries) {
    const key = `${entry.kind}:${entry.qualifier ?? ""}:${entry.value.toLowerCase()}`;
    if (seenEntries.has(key)) {
      findings.push({
        code: "manifest.entry.duplicate",
        severity: "info",
        title: `${entry.value} is declared twice`,
        detail:
          "Harmless at load time, but a repeated entry is nearly always a paste error standing where " +
          "a different path was meant to be. Check whether one of the two should say something else.",
        ...at(entry),
        entity: manifestEntity,
      });
    }
    seenEntries.add(key);
  }

  // ── Declared paths ───────────────────────────────────────────────────────

  for (const entry of declaredPaths(manifest)) {
    const value = entry.value;

    // `@ox_lib/init.lua` is a path in somebody else's resource. It is the single
    // most common false "missing file" in this whole category.
    if (parseResourceInclude(value)) continue;

    if (isGlobPattern(value)) {
      const pattern = manifestPatternToRegExp(value);
      const matched = originalPaths.some((path) => pattern.test(path));
      if (!matched) {
        findings.push({
          code: "manifest.path.missing",
          severity: "warning",
          title: `${value} matches no file in this resource`,
          detail:
            "FiveM expands this pattern at load time and ships whatever it finds. It currently finds " +
            "nothing, so the declaration has no effect. Check the folder name and the extension.",
          ...at(entry),
          entity: manifestEntity,
        });
      }
      continue;
    }

    if (byKey.has(pathKey(value))) {
      const exact = originalPaths.includes(value.replace(/\\/g, "/").replace(/^\.\//, ""));
      const cased = exact ? null : caseOnlyMatch(value, originalPaths);
      if (cased) {
        findings.push({
          code: "manifest.path.case_mismatch",
          severity: "error",
          title: `${value} is on disk as ${cased}`,
          detail:
            "Windows does not care about the difference and Linux does, so this loads on your machine " +
            `and fails on the server. Change the manifest to '${cased}', or rename the file.`,
          ...at(entry),
          entity: manifestEntity,
          fix: { label: `Use '${cased}' in the manifest`, confidence: "high" },
        });
      }
      continue;
    }

    const suggestion = closestMatch(baseName(value), originalPaths.map(baseName));
    findings.push({
      code: "manifest.path.missing",
      severity: MISSING_PATH_SEVERITY[entry.kind] ?? "warning",
      title: `${value} is declared but not in this resource`,
      detail:
        `The manifest lists it under \`${entry.key}\`, and there is no such file here. ` +
        (entry.kind === "file"
          ? "It will simply not ship."
          : "The resource will fail to start.") +
        (suggestion ? ` Did you mean ${suggestion.value}?` : ""),
      ...at(entry),
      entity: manifestEntity,
      fix: suggestion
        ? { label: `Point it at ${suggestion.value}`, confidence: "medium" }
        : undefined,
    });
  }

  // ── data_file lines ──────────────────────────────────────────────────────

  const exported = new Set(entriesOfKind(manifest, "file").map((entry) => pathKey(entry.value)));
  const exportedPatterns = entriesOfKind(manifest, "file")
    .filter((entry) => isGlobPattern(entry.value))
    .map((entry) => manifestPatternToRegExp(entry.value));

  const isExported = (value: string) =>
    exported.has(pathKey(value)) || exportedPatterns.some((pattern) => pattern.test(value));

  for (const entry of entriesOfKind(manifest, "data_file")) {
    const type = entry.qualifier ?? "";

    if (!isKnownDataFileType(type)) {
      findings.push({
        code: "manifest.data_file.unknown_type",
        severity: "warning",
        title: `${type} is not a data_file type this tool recognises`,
        detail:
          "FiveM matches the type string against a fixed list and ignores anything that is not on it, " +
          "silently, so a typo here loads nothing and reports nothing. Check the spelling against the " +
          "type your file needs. This list is not exhaustive, so a rare-but-valid type will also land here.",
        ...at(entry),
        entity: manifestEntity,
      });
    }

    if (!isExported(entry.value) && !isGlobPattern(entry.value)) {
      findings.push({
        code: "manifest.data_file.not_exported",
        severity: "warning",
        title: `${entry.value} is a data_file but is not in files{}`,
        detail:
          `FiveM only ships files listed in files{}. Add '${entry.value}' there, or the ${type} ` +
          "declaration registers a file the client never receives.",
        ...at(entry),
        entity: manifestEntity,
        fix: { label: `Add '${entry.value}' to files{}`, confidence: "high" },
      });
    }

    // The type says what the XML root must be. A well-formed file under the
    // wrong type loads nothing and says nothing, which is the worst combination
    // a bug can have.
    const expectedRoot = expectedRootFor(type);
    const text = input.texts.get(pathKey(entry.value));
    if (expectedRoot && text !== undefined) {
      const actualRoot = rootElementName(text);
      if (actualRoot && actualRoot !== expectedRoot) {
        findings.push({
          code: "manifest.data_file.wrong_root",
          severity: "error",
          title: `${entry.value} is declared as ${type} but is a <${actualRoot}>`,
          detail:
            `A ${type} must have a <${expectedRoot}> root. The game will not read this file as ` +
            "declared, and nothing in the console will say so.",
          file: entry.value,
          entity: manifestEntity,
        });
      }
    }
  }

  // ── Metas on disk that nothing registers ─────────────────────────────────

  const registered = new Set(
    entriesOfKind(manifest, "data_file").map((entry) => pathKey(entry.value)),
  );
  const registeredPatterns = entriesOfKind(manifest, "data_file")
    .filter((entry) => isGlobPattern(entry.value))
    .map((entry) => manifestPatternToRegExp(entry.value));

  for (const file of input.files) {
    // `stream/` is exempt: FiveM streams anything in a folder called stream
    // automatically, so those assets are correct precisely because they are
    // undeclared.
    if (isInStreamFolder(file.path)) continue;
    if (isStreamExtension(file.extension)) continue;
    const kind = dataFileKindFor(file.name);
    if (!kind) continue;
    if (registered.has(pathKey(file.path))) continue;
    if (registeredPatterns.some((pattern) => pattern.test(file.path))) continue;

    findings.push({
      code: "manifest.meta.not_registered",
      severity: "error",
      title: `${file.path} is never loaded`,
      detail:
        `Nothing in ${manifest.file} registers it, so the game never reads it and the resource ` +
        `behaves as though the file were not there. Add: data_file '${kind}' '${file.path}'`,
      file: manifest.file,
      entity: manifestEntity,
      fix: { label: `Add data_file '${kind}' '${file.path}'`, confidence: "high" },
    });
  }

  // ── A resource inside a resource ─────────────────────────────────────────

  for (const file of input.files) {
    if (!isManifestFileName(file.name)) continue;
    if (!file.path.includes("/")) continue;
    findings.push({
      code: "manifest.nested_resource",
      severity: "warning",
      title: `${file.path} is a second resource inside this one`,
      detail:
        "FiveM starts one resource per folder with a manifest, and a manifest nested inside another " +
        "resource is never started. Move this folder out to sit beside its parent, or delete it if it " +
        "was left over from unpacking.",
      file: file.path,
      entity: manifestEntity,
    });
  }

  if (manifest.unparsed.length > 0) {
    skipped.push(
      `${manifest.unparsed.length} manifest statement${manifest.unparsed.length === 1 ? "" : "s"} ` +
        "could not be read, so anything they declare was not checked: " +
        manifest.unparsed.slice(0, 5).join("; "),
    );
  }

  return { findings, skipped };
}

/**
 * The root element name of an XML document, without building one.
 *
 * A full parse here would be wasted: this runs over every declared data file to
 * answer a question the first tag already settles, and `analyzeVehicleMeta`
 * parses the ones that matter properly anyway.
 */
function rootElementName(text: string): string | null {
  const match = /<\s*([A-Za-z_][\w.:-]*)/.exec(text.replace(/<\?[\s\S]*?\?>/g, "").replace(/<!--[\s\S]*?-->/g, ""));
  return match ? match[1] : null;
}

// ─── One resource ────────────────────────────────────────────────────────────

export function analyzeResource(
  input: GtaResourceInput,
  options: AnalyzeOptions = {},
): GtaResourceReport {
  const findings: GtaFinding[] = [];
  const skipped: string[] = [];

  // ── Manifest ─────────────────────────────────────────────────────────────

  let manifest: ParsedManifest | null = null;
  for (const candidate of MANIFEST_FILE_NAMES) {
    const source = input.texts.get(candidate);
    if (source === undefined) continue;
    manifest = parseManifest(candidate, source);
    break;
  }

  if (manifest) {
    const result = analyzeManifest(input, manifest);
    findings.push(...result.findings);
    skipped.push(...result.skipped);
  }

  // ── Stream assets ────────────────────────────────────────────────────────

  // Identity and origin ride along even though one resource cannot collide with
  // itself: this report's `stream` is what a surface renders and what a set-level
  // export reads back, and an occurrence that knows which archive it came from in
  // one code path and not the other is a difference somebody eventually has to
  // debug.
  // The sink is passed even here, where one resource can never hit the resource
  // cap, so that the two call sites cannot drift into reporting different things
  // about the same collector.
  const streamSkipped: string[] = [];
  const stream = collectStreamAssets(
    [
      {
        name: input.name,
        identity: input.identity,
        origin: input.origin,
        files: input.files,
      },
    ],
    { skipped: streamSkipped },
  );
  const streamResult = analyzeStreamAssets(stream, {
    vanillaNames: options.vanillaNames,
    labels: options.resourceLabels,
  });
  findings.push(...streamResult.findings);
  skipped.push(...streamSkipped, ...streamResult.skipped);

  // ── Vehicle metadata ─────────────────────────────────────────────────────

  const streamNames = new Set(
    input.files.filter((f) => isInStreamFolder(f.path)).map((f) => f.name.toLowerCase()),
  );
  const vehicleResult = analyzeVehicleMeta({
    texts: input.texts,
    streamNames,
    vanillaNames: options.vanillaNames,
  });
  findings.push(...vehicleResult.findings);
  skipped.push(...vehicleResult.skipped);

  // ── Dependencies ─────────────────────────────────────────────────────────

  let dependencies: DependencyGraph | null = null;
  if (manifest) {
    dependencies = buildDependencyGraph([
      { name: input.name, manifest, scripts: luaSources(input) },
    ]);
    findings.push(...dependencyFindings(dependencies));
  }

  // ── Shape of the folder ──────────────────────────────────────────────────

  const kind: GtaResourceReport["kind"] = manifest
    ? "fivem-resource"
    : stream.length > 0 || vehicleResult.vehicles.length > 0
      ? "asset-folder"
      : "folder";

  if (!manifest && kind === "asset-folder") {
    findings.push({
      code: "manifest.missing",
      severity: "warning",
      title: "No manifest",
      detail:
        "This folder holds game assets but has no fxmanifest.lua, so FiveM cannot load it as a " +
        "resource. It may be a fragment meant to be merged into one, or the manifest may be missing.",
      // Identity, not name, and it falls back to the name when there is none. A
      // surface that groups a whole set by entity — `FindingsList` does — would
      // otherwise file both halves of a two-`police` drop under one heading, and
      // the reader sees one heading carrying two identical rows with nothing to
      // say which pack either belongs to.
      entity: { kind: "resource", id: input.identity || input.name },
    });
  }

  const ordered = sortFindings(dedupeFindings(findings));

  return {
    name: input.name,
    // Carried through verbatim, including the absence of an origin. The report
    // is the last thing that knows which of two identically-named packs this
    // was, and an exporter that has to guess is an exporter that prints two
    // identical headings.
    identity: input.identity,
    origin: input.origin,
    kind,
    verdict: verdictFor([...ordered, ...vehicleResult.vehicles.flatMap((v) => v.findings)]),
    manifest,
    vehicles: vehicleResult.vehicles,
    dependencies,
    stream,
    checks: buildChecks(vehicleResult.vehicles, stream),
    findings: ordered,
    skipped,
  };
}

/** Every `.lua` in the resource, for the inferred-dependency scan. */
function luaSources(input: GtaResourceInput): { file: string; source: string }[] {
  const out: { file: string; source: string }[] = [];
  for (const [path, source] of input.texts) {
    if (path.endsWith(".lua") && !isManifestFileName(baseName(path))) {
      out.push({ file: path, source });
    }
  }
  return out;
}

/**
 * The headline counters — "Vehicle assets resolved: 28 of 28".
 *
 * These are what a user reads first, and they are deliberately about resolution
 * rather than about findings: "28 of 28" says the cross-reference completed,
 * where "no errors" could also mean nothing was checked.
 */
function buildChecks(
  vehicles: VehicleEntity[],
  stream: StreamAsset[],
): GtaResourceReport["checks"] {
  const checks: GtaResourceReport["checks"] = [];

  if (vehicles.length > 0) {
    const resolved = vehicles.filter(
      (vehicle) => !vehicle.findings.some((finding) => finding.severity === "error"),
    ).length;
    checks.push({ label: "Vehicles fully resolved", passed: resolved, total: vehicles.length });
  }

  if (stream.length > 0) {
    const clean = stream.filter((asset) => asset.occurrences.length === 1).length;
    checks.push({ label: "Stream names unambiguous", passed: clean, total: stream.length });
  }

  return checks;
}

// ─── A set of resources ──────────────────────────────────────────────────────

export type GtaResourceSetReport = {
  resources: GtaResourceReport[];
  /** Findings that only exist between resources: collisions, cycles, missing deps. */
  findings: GtaFinding[];
  dependencies: DependencyGraph;
  stream: StreamAsset[];
  skipped: string[];
};

/**
 * Analyse a whole `resources/` folder, or a pack about to be exported.
 *
 * The checks that only exist here are the ones no single resource can see. Two
 * resources shipping `police.yft` are each individually correct; a dependency
 * cycle needs both halves; and whether `es_extended` is missing depends on
 * whether anything in the set provides it.
 */
export function analyzeResourceSet(
  inputs: GtaResourceInput[],
  options: AnalyzeOptions = {},
): GtaResourceSetReport {
  // Decided once, here, and handed to every pass below. This is the only place
  // in the chain that can see all the resources at once, which is the only
  // vantage point from which "is this name ambiguous?" has an answer — and every
  // sentence in the finished report has to agree on that answer, or a reader
  // matches a cross-resource finding against a per-resource one and cannot tell
  // whether they are about the same pack.
  const labels = new Map<string, string>(
    disambiguateLabels(
      inputs.map((input) => ({
        name: input.name,
        identity: input.identity,
        origin: input.origin,
      })),
      PROSE_LABEL_STYLE,
    ).map((label, index) => [
      normalizeName(inputs[index].identity ?? inputs[index].name),
      label,
    ]),
  );
  const withLabels: AnalyzeOptions = { ...options, resourceLabels: labels };
  const resources = inputs.map((input) => analyzeResource(input, withLabels));

  // What the collector could not fold in goes into the set's `skipped` beside
  // what the analysis could not check, because both answer the one question the
  // report's footer asks: is this namespace clean, or did we stop looking?
  const streamSkipped: string[] = [];
  const stream = collectStreamAssets(
    inputs.map((input) => ({
      name: input.name,
      identity: input.identity,
      origin: input.origin,
      files: input.files,
    })),
    { skipped: streamSkipped },
  );
  const streamResult = analyzeStreamAssets(stream, {
    vanillaNames: options.vanillaNames,
    labels,
  });

  // Deliberately keyed on `name`, not `identity`, and this is the one place in
  // the chain where that is right. A `dependency 'police'` line names a FiveM
  // resource name, and FiveM resolves it by name — a graph keyed on drop-relative
  // roots would report every satisfied dependency as missing. That two packs can
  // both claim the name is a real ambiguity, and it is one the *server* has too:
  // the collision findings above are where it gets reported, not here.
  const dependencies = buildDependencyGraph(
    inputs.map((input, index) => ({
      name: input.name,
      manifest: resources[index].manifest,
      scripts: luaSources(input),
    })),
  );

  const crossResource = sortFindings(
    dedupeFindings([...streamResult.findings, ...dependencyFindings(dependencies)]),
  );

  return {
    resources,
    findings: crossResource,
    dependencies,
    stream,
    skipped: [
      ...new Set([
        ...streamSkipped,
        ...streamResult.skipped,
        ...resources.flatMap((r) => r.skipped),
      ]),
    ],
  };
}
